import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Volume2Icon, VolumeXIcon } from 'lucide-react';
import { HouseModeChip } from '../ui/HouseModeChip';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { useDb } from '../../hooks/useDb';
import { rainNext2h, roomById, weatherNow } from '../../utils/dbSelectors';
import type { AnnaStage } from './useAnnaStage';
interface WallTopBarProps {
  stage: AnnaStage;
  /** Nothing going on: show only the time */
  calm: boolean;
}

/** Top edge, very quiet: time, weather + rain next 2 h, house mode, room, connection dots. */
export function WallTopBar({
  stage,
  calm
}: WallTopBarProps) {
  const {
    soundOn,
    setSoundOn
  } = useSpecSettings();
  const {
    meta,
    weather,
    rain,
    room
  } = useDb(t => ({
    meta: t.meta,
    weather: weatherNow(t),
    rain: rainNext2h(t),
    room: roomById(t, t.meta.roomId)
  }));
  const sleep = stage.view === 'sleep';
  const showDetails = !sleep && !calm;
  const maxRain = Math.max(1, ...rain.quarters);
  const down = (Object.entries(meta.connections) as [string, boolean][]).filter(([, ok]) => !ok);
  return <header className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-6 px-6 pt-14 lg:px-10" data-id="element-1583" data-mp="2cc42odyvu2ff">
      <div className="pointer-events-auto flex items-end gap-8" data-id="element-1584" data-mp="23cqox93q6row">
        {/* Dim clock in Slapen stays at 6:1 contrast — dimmed by colour, not opacity */}
        <time className={`font-display text-[56px] font-extralight leading-none tabular-nums lg:text-[72px] ${sleep ? 'text-ink-muted' : 'text-ink'}`} data-id="element-1585" data-mp="302hmxib86wzg">
          {meta.time}
        </time>
        <AnimatePresence data-id="element-1586" data-mp="2r107m25n6zbt">
          {showDetails && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="hidden pb-1 sm:block" data-id="element-1587" data-mp="2m3i8f3hffyh0">
              <p className="text-[22px] text-ink-soft" data-id="element-1588" data-mp="1an9zmkbejqww">
                {weather.temp}° · {weather.sky}
              </p>
              <div className="mt-1 flex items-end gap-3" data-id="element-1589" data-mp="1c35qc1u3vtm4">
                <div className="flex h-6 items-end gap-[3px]" aria-hidden data-id="element-1590" data-mp="1h3a8rxekqyez">
                  {rain.quarters.map((mm, i) => <span key={i} className={`w-1.5 rounded-sm ${mm > 0 ? 'bg-[#8FB8D8]' : 'bg-white/15'}`} style={{
                height: `${Math.max(3, mm / maxRain * 24)}px`
              }} data-id="element-1591" data-mp="134akiquz4ljc" data-mpi={i} />)}
                </div>
                <span className="text-[22px] text-ink-muted" data-id="element-1592" data-mp="y723itlkanjo">{rain.note}</span>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1593" data-mp="ogpiz1dqyudj">
        {showDetails && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="pointer-events-auto flex flex-wrap items-center justify-end gap-x-5 gap-y-3" data-id="element-1594" data-mp="odbdvldp872e">
            <HouseModeChip mode={stage.mode} active size="wall" data-id="element-1595" data-mp="3s0ochr19tdwg" />
            <span className="hidden text-[22px] text-ink-soft md:inline" data-id="element-1596" data-mp="3n0ipwp9uyhl8">{room?.name}</span>
            <ul className="flex items-center gap-2" aria-label="Verbindingen" data-id="element-1597" data-mp="4uxeff3imaz6">
              {(Object.entries(meta.connections) as [string, boolean][]).map(([name, ok]) => <li key={name} className="flex items-center" data-id="element-1598" data-mp="ers9fo0kxt8p">
                  <span className={`h-2 w-2 rounded-full ${ok ? 'bg-accent/80' : 'bg-amber'}`} aria-hidden data-id="element-1599" data-mp="epz3yc1pdf2a" />
                  <span className="sr-only" data-id="element-1600" data-mp="9r4lxcbk7uj8">{`${name}: ${ok ? 'verbonden' : 'weg'}`}</span>
                </li>)}
            </ul>
            {down.length > 0 && <span className="text-[22px] text-amber" data-id="element-1601" data-mp="jk3aiu723l2u">{down.map(([n]) => n).join(', ')} weg</span>}
            <button type="button" onClick={() => setSoundOn(!soundOn)} aria-label={soundOn ? 'Geluid en stem uit' : 'Geluid en stem aan'} className="grid h-12 w-12 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1602" data-mp="xjbes1i2tjdw">
              {soundOn ? <Volume2Icon className="h-6 w-6" data-id="element-1603" data-mp="2wpqdwqb8p6uo" /> : <VolumeXIcon className="h-6 w-6" data-id="element-1604" data-mp="2wqrs33njy9gm" />}
            </button>
          </motion.div>}
      </AnimatePresence>
    </header>;
}